import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMobileLayout } from './app/useMobileLayout';
import { useTrailData } from './data/useTrailData';
import {
  buildCalendarDays,
  inclusiveDayCount,
  numberFormat,
  restDayLocations,
  wholeNumberFormat,
  type CalendarDay,
} from './domain/trail';
import { ActivityDrawer } from './features/day/ActivityDrawer';
import { TrailMap } from './features/map/TrailMap';
import { activityIdSchema, walkDateSchema, type Photo } from './schemas';
import { Experience, MapTools, OverviewTitle, Stats, StoryPanel } from './styles';

export function App() {
  const { route, photos, activitiesByDate, activitiesById } = useTrailData();
  const [selectedDayDate, setSelectedDayDate] = useState<string | null>(null);
  const [spotlightPhotoId, setSpotlightPhotoId] = useState<string | null>(null);
  const [highlightedPhotoId, setHighlightedPhotoId] = useState<string | null>(null);
  const [elevationLocation, setElevationLocation] = useState<[number, number] | null>(null);
  const [isThreeDimensional, setIsThreeDimensional] = useState(true);
  const [wholeRouteRequest, setWholeRouteRequest] = useState(0);
  const selectionHydratedRef = useRef(false);
  const isMobile = useMobileLayout();

  const selectedActivity = selectedDayDate ? (activitiesByDate.get(selectedDayDate) ?? null) : null;
  const selectedRestLocation = selectedDayDate ? restDayLocations[selectedDayDate] : undefined;
  const selectedRestDay = useMemo(
    () =>
      selectedDayDate && !selectedActivity && selectedRestLocation
        ? { date: selectedDayDate, location: selectedRestLocation }
        : null,
    [selectedActivity, selectedDayDate, selectedRestLocation],
  );
  const dayPhotos = useMemo(
    () => (selectedDayDate ? photos.filter((photo) => photo.capture_date === selectedDayDate) : []),
    [photos, selectedDayDate],
  );
  const calendarDays = useMemo(
    () => (route ? buildCalendarDays(route.metadata.started, route.metadata.finished, activitiesByDate) : []),
    [activitiesByDate, route],
  );
  const selectedDayIndex = selectedDayDate
    ? calendarDays.findIndex((day) => day.date === selectedDayDate)
    : -1;
  const previousDay = selectedDayIndex > 0 ? (calendarDays[selectedDayIndex - 1] ?? null) : null;
  const nextDay = selectedDayIndex >= 0 ? (calendarDays[selectedDayIndex + 1] ?? null) : null;

  const clearTransientHighlights = useCallback(() => {
    setHighlightedPhotoId(null);
    setElevationLocation(null);
  }, []);

  const openDayDate = useCallback(
    (date: string) => {
      if (!activitiesByDate.has(date) && !restDayLocations[date]) return;
      clearTransientHighlights();
      setSelectedDayDate(date);
    },
    [activitiesByDate, clearTransientHighlights],
  );

  const closeDay = useCallback(() => {
    clearTransientHighlights();
    setSelectedDayDate(null);
    setSpotlightPhotoId(null);
  }, [clearTransientHighlights]);

  useEffect(() => {
    if (!route) return;
    const url = new URL(window.location.href);

    if (!selectionHydratedRef.current) {
      selectionHydratedRef.current = true;
      const requestedActivityId = activityIdSchema.safeParse(url.searchParams.get('activity'));
      if (requestedActivityId.success) {
        const activity = activitiesById.get(requestedActivityId.data);
        if (activity && activity.date !== selectedDayDate) {
          setSelectedDayDate(activity.date);
          return;
        }
      } else {
        const requestedDay = walkDateSchema.safeParse(url.searchParams.get('day'));
        if (
          requestedDay.success &&
          restDayLocations[requestedDay.data] &&
          requestedDay.data !== selectedDayDate
        ) {
          setSelectedDayDate(requestedDay.data);
          return;
        }
      }
    }

    if (selectedActivity) {
      url.searchParams.set('activity', selectedActivity.activity_id);
      url.searchParams.delete('day');
    } else if (selectedRestDay) {
      url.searchParams.delete('activity');
      url.searchParams.set('day', selectedRestDay.date);
    } else {
      url.searchParams.delete('activity');
      url.searchParams.delete('day');
    }
    window.history.replaceState(null, '', url);
  }, [activitiesById, route, selectedActivity, selectedDayDate, selectedRestDay]);

  const handleActivitySelect = useCallback(
    (activityId: string | null) => {
      if (!activityId) {
        closeDay();
        return;
      }
      const activity = activitiesById.get(activityId);
      if (!activity) return;
      if (selectedActivity?.activity_id === activityId) closeDay();
      else openDayDate(activity.date);
    },
    [activitiesById, closeDay, openDayDate, selectedActivity?.activity_id],
  );

  const handlePhotoOpen = useCallback(
    (photo: Photo) => {
      openDayDate(photo.capture_date);
      setSpotlightPhotoId(photo.id);
    },
    [openDayDate],
  );

  const showWholeRoute = () => {
    closeDay();
    setWholeRouteRequest((request) => request + 1);
  };

  return (
    <>
      <Experience $drawerOpen={selectedDayDate !== null}>
        <TrailMap
          route={route}
          photos={photos}
          selectedDayDate={selectedDayDate}
          selectedActivity={selectedActivity}
          selectedRestDay={selectedRestDay}
          selectedDayPhotos={dayPhotos}
          highlightedPhotoId={highlightedPhotoId}
          elevationLocation={elevationLocation}
          isThreeDimensional={isThreeDimensional}
          wholeRouteRequest={wholeRouteRequest}
          onActivitySelect={handleActivitySelect}
          onPhotoOpen={handlePhotoOpen}
        />

        {!selectedDayDate && (
          <StoryPanel aria-live="polite">
            <OverviewTitle>2023 Te Araroa</OverviewTitle>
            <Stats>
              <div>
                <dt>Distance</dt>
                <dd>{route ? `${wholeNumberFormat.format(route.metadata.distanceKm)} km` : '—'}</dd>
              </div>
              <div>
                <dt>Elevation gain</dt>
                <dd>{route ? `${numberFormat.format(route.metadata.elevationGainM)} m` : '—'}</dd>
              </div>
              <div>
                <dt>Days taken</dt>
                <dd>
                  {route
                    ? numberFormat.format(inclusiveDayCount(route.metadata.started, route.metadata.finished))
                    : '—'}
                </dd>
              </div>
            </Stats>
          </StoryPanel>
        )}

        <MapTools aria-label="Map view controls">
          <button
            type="button"
            aria-pressed={isThreeDimensional}
            onClick={() => setIsThreeDimensional((current) => !current)}
          >
            {isThreeDimensional ? '3D relief' : '2D atlas'}
          </button>
          <button type="button" onClick={showWholeRoute}>
            Whole route
          </button>
        </MapTools>
      </Experience>

      <ActivityDrawer
        activity={selectedActivity}
        restDay={selectedRestDay}
        photos={dayPhotos}
        journeyPhotos={photos}
        previousDay={previousDay}
        nextDay={nextDay}
        spotlightPhotoId={spotlightPhotoId}
        isMobile={isMobile}
        onClose={closeDay}
        onNavigate={(day: CalendarDay) => openDayDate(day.date)}
        onSpotlightDayChange={openDayDate}
        onSpotlightClose={() => setSpotlightPhotoId(null)}
        onPhotoMarkerHighlightChange={setHighlightedPhotoId}
        onElevationLocationChange={setElevationLocation}
      />
    </>
  );
}
